import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';

import { AgentBuilder } from '@components/agents';
import { Button } from '@components/ui/button';
import { Card } from '@components/ui/card';
import { queryKeys } from '@constants/queryKeys.constant';
import { getErrorMessage } from '@helpers/getErrorMessage.helper';
import {
  agentCapabilitiesQueryOptions,
  agentConfigurationQueryOptions,
  agentDetailQueryOptions,
  agentEditorDataQueryOptions,
  agentKnowledgeSelectionQueryOptions,
  agentVersionsQueryOptions,
  knowledgeBasesQueryOptions,
} from '@services/agents/queryOptions';

export const Route = createFileRoute('/_dashboard/agents/$agentId/')({
  component: EditAgentPage,
  errorComponent: AgentEditorError,
  loader: async ({ context, params }) => {
    const [editorData] = await Promise.all([
      context.queryClient.ensureQueryData(
        agentEditorDataQueryOptions(params.agentId),
      ),
      context.queryClient.ensureQueryData(
        agentDetailQueryOptions(params.agentId),
      ),
    ]);
    context.queryClient.setQueryData(
      queryKeys.agents.capabilities(),
      editorData.capabilities,
    );
    context.queryClient.setQueryData(
      queryKeys.agents.configuration(params.agentId),
      editorData.configuration,
    );
    context.queryClient.setQueryData(
      queryKeys.agents.knowledgeBases(),
      editorData.knowledgeBases,
    );
    context.queryClient.setQueryData(
      queryKeys.agents.knowledgeSelection(params.agentId),
      editorData.knowledgeSelection,
    );
    context.queryClient.setQueryData(
      queryKeys.agents.versions(params.agentId),
      editorData.versions,
    );
  },
});

function AgentEditorError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  function handleRetry() {
    reset();
    void router.invalidate();
  }

  return (
    <section className="grid min-h-full place-items-center p-6">
      <Card className="grid max-w-xl gap-4 text-center">
        <div>
          <h1 className="text-primary-dark text-2xl font-extrabold">
            Couldn&apos;t load the agent editor
          </h1>
          <p className="text-muted mt-2 text-sm leading-6">
            {getErrorMessage(error)}
          </p>
        </div>
        <div>
          <Button
            onClick={handleRetry}
            textTransform="none"
            type="button"
            variant="primary"
          >
            Try again
          </Button>
        </div>
      </Card>
    </section>
  );
}

function EditAgentPage() {
  const { agentId } = Route.useParams();
  const { data: capabilities } = useQuery(agentCapabilitiesQueryOptions());
  const { data: configuration } = useQuery(
    agentConfigurationQueryOptions(agentId),
  );
  const { data: agent } = useQuery(agentDetailQueryOptions(agentId));
  const { data: knowledgeSelection } = useQuery(
    agentKnowledgeSelectionQueryOptions(agentId),
  );
  const { data: knowledgeBases } = useQuery(knowledgeBasesQueryOptions());
  const { data: versions } = useQuery(agentVersionsQueryOptions(agentId));

  if (
    !agent ||
    !capabilities ||
    !configuration ||
    !knowledgeSelection ||
    !knowledgeBases ||
    !versions
  ) {
    return null;
  }

  return (
    <AgentBuilder
      capabilities={capabilities}
      configuration={configuration}
      key={agentId}
      knowledgeBases={knowledgeBases}
      knowledgeSelection={knowledgeSelection}
      versions={versions}
      workflowMode={agent.workflowMode}
    />
  );
}
